// The rank ladder (plan §17.5 `TIERS`) as typed config — invariant 6: no band literal
// appears in code that reads one. Pure data plus one total function, so every fence stays
// clean.
//
// This lives in SHARED rather than the server (where D197 first wrote it) because rank is
// presentation as much as it is rating: the dashboard rail, the promotion and the table
// badge all need to know which band a rating falls in, and a second copy of the table in
// the client is a copy that can disagree with the ladder. Same reasoning as serverConfig.
//
// Note what is NOT here. Display NAMES ("Squire") are presentation and live in the client,
// the same split D154 drew for card ids — the engine and the wire speak `squire`. And the
// rail's virtual span for the two open-ended bands (beggar has no floor, emperor no
// ceiling) is a DRAWING decision, not a rating one; it belongs to the component that draws
// the bar, or the ladder acquires bounds it does not actually have.

/** A band, identified by the rating at which it opens. `null` ⇒ unbounded below. */
export interface RankBand {
  readonly id: RankId;
  /** Lowest rating in the band; null for the bottom band, which has no floor. */
  readonly floor: number | null;
}

export type RankId = 'beggar' | 'peasant' | 'squire' | 'knight' | 'duke' | 'king' | 'emperor';

/**
 * §17.5, lowest first. Bands are 150 wide: a 150-point gap is roughly a 70/30 favourite
 * head to head, so each step up is a claim about skill rather than a decoration, and the
 * §17.5 matchmaking base window (±150) spans exactly one band.
 *
 * `RATING.initR` (1300) sits 100 INTO squire and 50 below knight ON PURPOSE (D197): a
 * fresh 4-player game moves a provisional account about ±162, so a first win promotes and
 * only last place demotes — and never past peasant. Beggar is 250 below the start and
 * cannot be reached by bad luck alone.
 */
export const RANKS: readonly RankBand[] = [
  { id: 'beggar', floor: null },
  { id: 'peasant', floor: 1050 },
  { id: 'squire', floor: 1200 },
  { id: 'knight', floor: 1350 },
  { id: 'duke', floor: 1500 },
  { id: 'king', floor: 1650 },
  { id: 'emperor', floor: 1800 },
];

/** The highest band whose floor is at or below `rating`. Total: the bottom band has no floor. */
export function rankFor(rating: number): RankId {
  let id = RANKS[0]!.id;
  for (const band of RANKS) if (band.floor === null || rating >= band.floor) id = band.id;
  return id;
}

/** Ladder position, 0-based. Comparing two ranks is comparing these, never the ids. */
export function rankIndex(id: RankId): number {
  return RANKS.findIndex((band) => band.id === id);
}

/**
 * Rated games before a rank is AWARDED (H14, the user's call 2026-08-30).
 *
 * The rating itself moves from the very first game — this gates DISPLAY, never the maths.
 * Two things fall out of that, and both are the point:
 *   * a fresh account cannot appear on the leaderboard having played nothing, which is what
 *     made [U1]'s verbatim guest-rank transfer safe to ship at all;
 *   * an unplaced player is shown a goal ("2 more games to earn your rank") rather than a
 *     number they have no reason to trust after one game — at RD 350 a single result moves
 *     a rating ~160 points, so the first two are nearly noise.
 * An unplaced player is not shown their rating AT ALL, by the same argument.
 */
export const PLACEMENT_GAMES = 3;

/** Has this player finished enough rated games to be awarded a rank? */
export function isPlaced(ratedGames: number): boolean {
  return ratedGames >= PLACEMENT_GAMES;
}
